// Resource ceiling checks for the plan page (ENG-6065). Every ceiling is advisory: nothing
// downstream refuses a route over one, so the warning is all that stands behind it. A null or zero
// ceiling means no check.

import { calcItemCount, calcItemVolume, calcItemWeight } from '@/utilities/calculateItemMetrics';
import { CEILINGS } from '@/components/Resources/constants';
import { equipmentKeys } from '@/components/Resources/utils';
import { recommendedEquipmentLabels } from '@/constants/recommendedEquipmentLabels';
import { LOCATION_TYPES } from '@/constants/locationTypes';
import { routeDisplayName, routeName } from '@/utilities/routeNames';

import { computeProjectedCustomerStops } from './routePreviewStats';

// Equipment and service level are tagged apart from the ceilings because they cannot share a sentence:
// describeExceeded() formats "Label (used / limit unit)", which has no way to say "needs a lift
// gate", and "you have exceeded the resource ceiling on" is wrong for both of them.
export const ADVISORY_KINDS = {
    CEILING: 'ceiling',
    EQUIPMENT: 'equipment',
    SERVICE: 'service',
};

export const routeLabel = (route) => routeName(route) || route?.route_number || 'route';

// Crossdock pickup legs are excluded: only the delivery leg books an order onto a resource, so the
// ceilings count it there, and a warehouse pickup doesn't need the lift gate or service level the
// delivery does.
//
// The two inputs identify the leg differently — `route.orders` are mappings carrying `type`, while
// `addedOrders` are planning rows carrying the synthesised `crossdock_leg`.
const isPickupMapping = (mapping) => mapping?.type === 'PICKUP';
const isPickupRow = (order) => order?.crossdock_leg === 'pickup';

// The orders every check reads, so they share the leg exclusion rather than each re-deriving it.
// `addedOrders` projects a pending assignment, so the assign path can check before it writes.
export const routeOrders = (route, addedOrders = []) =>
    [
        ...(route?.orders || []).filter((mapping) => !isPickupMapping(mapping)).map((mapping) => mapping.order),
        ...addedOrders.filter((order) => !isPickupRow(order)),
    ].filter(Boolean);

// Cubes, weight and pieces are the flat sum of the route's orders.
export const routeLoad = (route, addedOrders = []) => {
    const orders = routeOrders(route, addedOrders);

    return {
        cubes: calcItemVolume(orders),
        weight: calcItemWeight(orders),
        stops: route ? computeProjectedCustomerStops(route, addedOrders) : 0,
        items: calcItemCount(orders),
    };
};

// Stable output order, so the same route always produces the same sentence — the natural order is
// whichever order happened to be read first, which reshuffles as a route is edited.
const inCanonicalOrder = (entries, canonical) => {
    const rank = (key) => {
        const idx = canonical.indexOf(key);

        return idx === -1 ? canonical.length : idx;
    };

    return [...entries].sort((a, b) => rank(a.key) - rank(b.key));
};

const EQUIPMENT_ORDER = Object.keys(recommendedEquipmentLabels);
const SERVICE_ORDER = Object.keys(LOCATION_TYPES);

// The merge `hooks/shapes.js` established: overrides land last and win, because a recommendation
// the planner turned off is an override holding an explicitly falsy value.
const orderEquipmentKeys = (order) => {
    const merged = { ...(order?.recommended_equipment || {}), ...(order?.equipment_overrides || {}) };

    return Object.keys(merged).filter((key) => merged[key]);
};

// `resources.equipment` is a jsonb map sharing its key vocabulary with the orders' equipment
// columns deliberately — that is why it is not a set of booleans on their own columns.
export const checkEquipment = (resource, orders = []) => {
    const carried = new Set(equipmentKeys(resource?.equipment));
    const needed = new Set(orders.flatMap(orderEquipmentKeys));

    return inCanonicalOrder(
        [...needed]
            .filter((key) => !carried.has(key))
            .map((key) => ({
                kind: ADVISORY_KINDS.EQUIPMENT,
                key,
                label: recommendedEquipmentLabels[key] || key,
            })),
        EQUIPMENT_ORDER
    );
};

// An empty `service_types` is the column's default and means "no restriction", which is not the
// same as "supports nothing" — it must never warn.
//
// Matched on `dropoff_location_type`, which every order carries. An order on a per-client service
// level shows that level's own name elsewhere in the UI but is matched on the location type
// underneath it, which is the gap accepted when this vocabulary was chosen.
export const checkServiceTypes = (resource, orders = []) => {
    const supported = resource?.service_types || [];

    if (supported.length === 0) {
        return [];
    }

    const needed = new Set(orders.map((order) => order?.dropoff_location_type).filter(Boolean));

    return inCanonicalOrder(
        [...needed]
            .filter((type) => !supported.includes(type))
            .map((type) => ({
                kind: ADVISORY_KINDS.SERVICE,
                key: type,
                label: LOCATION_TYPES[type] || type,
            })),
        SERVICE_ORDER
    );
};

export const checkRouteCeilings = (route, addedOrders = []) => {
    const resource = route?.resource;

    if (!resource) {
        return { route, resource: null, advisory: [] };
    }

    const load = routeLoad(route, addedOrders);

    const exceeded = CEILINGS.map((ceiling) => {
        const limit = Number(resource[ceiling.column] ?? 0) || 0;
        const used = load[ceiling.value] || 0;

        // A null or zero ceiling is "not configured", so there is nothing to be over.
        return limit > 0 && used > limit ? { kind: ADVISORY_KINDS.CEILING, ceiling, used, limit } : null;
    }).filter(Boolean);

    const orders = routeOrders(route, addedOrders);

    return {
        route,
        resource,
        advisory: [...exceeded, ...checkEquipment(resource, orders), ...checkServiceTypes(resource, orders)],
    };
};

export const checkRoutesCeilings = (routes = []) => {
    // Wrapped, not passed by reference: map's index argument would land in `addedOrders`.
    const results = routes.filter(Boolean).map((route) => checkRouteCeilings(route));

    return {
        results,
        warned: results.filter(({ advisory }) => advisory.length > 0),
    };
};

const round = (value) => Math.round(value).toLocaleString();

// "Cubic ft (1,420 / 1,200 cu ft)".
export const describeExceeded = ({ ceiling, used, limit }) =>
    `${ceiling.label} (${round(used)} / ${round(limit)} ${ceiling.short})`;

// A ceiling entry is the only one with a used/limit pair to report; the other two are already
// carrying the label they want printed.
export const describeAdvisory = (entry) =>
    entry.kind === ADVISORY_KINDS.CEILING ? describeExceeded(entry) : entry.label;

// `pick` selects one kind from each route's advisory list.
const describeRouteAdvisory = (entries, pick, showRoute) =>
    entries
        .map((entry) => pick(entry))
        .map((listedEntries, i) => {
            const listed = listedEntries.map(describeAdvisory).join(', ');

            return listed && showRoute ? `${routeLabel(entries[i].route)}: ${listed}` : listed;
        })
        .filter(Boolean)
        .join(' · ');

// One sentence per kind rather than three things forced under one lead-in that is only true of the
// first. A route can trip any combination, so every clause that has entries is emitted, in this
// order.
const ADVISORY_CLAUSES = [
    { kind: ADVISORY_KINDS.CEILING, lead: 'Resource ceiling exceeded for' },
    { kind: ADVISORY_KINDS.EQUIPMENT, lead: 'Resource missing equipment' },
    { kind: ADVISORY_KINDS.SERVICE, lead: 'Resource service level not configured' },
];

// A list rather than one string so each kind can render as its own row — three separate problems
// run together as a paragraph read as one.
export const advisoryLines = (entries, showRoute = false) =>
    ADVISORY_CLAUSES.map(({ kind, lead }) => {
        const listed = describeRouteAdvisory(
            entries,
            (entry) => (entry.advisory || []).filter((item) => item.kind === kind),
            showRoute
        );

        return listed ? `${lead}: ${listed}.` : null;
    }).filter(Boolean);

// One route's result in the list shape advisoryLines() reads.
export const asEntry = ({ route, advisory = [] }) => [{ route, advisory }];

// What to warn about when `route` moves to `nextResourceId` (null to clear it); `nextResource` is that
// resource's row, when it is loaded. Sharing a resource across two routes is legal but almost always a
// mis-click, so it warns without blocking. Shells are excluded, or every pick of an unused resource
// matches its own. The route's orders are checked against the resource it is moving to, the way
// assign checks them.
export const changeResourceWarnings = ({ route, routes = [], nextResourceId, nextResource = null }) => {
    const conflict = nextResourceId
        ? routes.find((r) => !r.shell && r.route_id !== route?.route_id && r.resource_id === nextResourceId)
        : null;
    const { advisory } = checkRouteCeilings({ ...route, resource: nextResource });

    return [
        ...(conflict
            ? [
                  `${nextResource?.name || 'That resource'} is already on ${routeDisplayName(
                      conflict
                  )} today. Both routes share its capacity.`,
              ]
            : []),
        ...advisoryLines(asEntry({ route, advisory })),
    ];
};

// Why a resource shut off for the day refuses a new booking.
export const shutoffMessage = (route) =>
    `${routeLabel(
        route
    )} is not accepting any more orders for the day. Orders already on its resource can still be added to its route.`;
